// src/pages/Careers.jsx
import React, { useState } from "react";
import { Briefcase, ExternalLink } from "lucide-react";
import { toast } from 'react-toastify';
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase1";
import { Helmet } from "react-helmet-async";
// Assuming you have a basic db setup like: import { db } from "../firebase1";
// We will assume 'db' is available from the context/setup file for the save operation to work.

// 🚨 IMPORTANT: Replace this placeholder URL with the deployed Google Apps Script Web App URL.
// The Apps Script will securely handle sending the email.
const GMAIL_SERVICE_URL = "YOUR_DEPLOYED_GOOGLE_APPS_SCRIPT_WEB_APP_URL"; 

// --- Mock Firestore DB (for context, remove if using real import) ---
/*
const db = {
    // Mock for demonstration purposes if "../firebase1" is missing
    get db() {
        console.warn("Using mock 'db'. Ensure 'db' is imported correctly for real storage.");
        return {};
    }
};
*/
// -------------------------------------------------------------------

const jobOpenings = [
    {
        title: "Senior NDT Technician",
        description: "Seeking an experienced NDT technician with expertise in multiple methods including UT, MT, and PT. Must be certified and ready to lead complex projects.",
        requirements: ["ASNT Level III certification", "5+ years of experience", "Strong communication and leadership skills"],
    },
    {
        title: "PAUT & TOFD Specialist",
        description: "Experienced professional required for advanced Ultrasonic Testing projects. Must be proficient in Phased Array Ultrasonic Testing (PAUT) and Time of Flight Diffraction (TOFD).",
        requirements: ["ASNT Level II or III in UT", "3+ years of PAUT/TOFD field experience", "Proficiency with advanced software"],
    },
    {
        title: "Conventional UT Technician",
        description: "Seeking a certified NDT technician with strong experience in conventional Ultrasonic Testing (UT) for various industrial applications.",
        requirements: ["ASNT Level II or III in UT", "3+ years of experience in UT", "Ability to interpret inspection data"],
    },
    {
        title: "Magnetic Particle & Penetrant Testing (MT/PT) Inspector",
        description: "An opportunity for a skilled inspector specializing in Magnetic Particle Testing (MT) and Penetrant Testing (PT).",
        requirements: ["ASNT Level II in MT and PT", "2+ years of relevant experience", "Attention to detail and a commitment to safety"],
    },
    {
        title: "Junior Inspector",
        description: "An entry-level position for a motivated individual looking to start a career in Non-Destructive Testing. Training and mentorship provided.",
        requirements: ["Technical degree or diploma", "Eager to learn", "Excellent attention to detail"],
    },
    {
        title: "Quality Assurance Engineer",
        description: "Responsible for developing and implementing quality control processes. This role ensures all inspection procedures meet industry standards and client specifications.",
        requirements: ["Degree in Engineering", "Knowledge of ISO 9001", "3+ years of QA experience"],
    },
];

const Careers = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        jobTitle: "",
        message: "",
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let firestoreSuccess = false;

        try {
            // 1. Save data to Firestore (ACTIVE LOGIC)
            // Assumes 'db' is correctly initialized and imported.
            // If 'db' is not available, this line will throw an error.
            await addDoc(collection(db, "FITCareerApplications"), {
                ...formData,
                createdAt: serverTimestamp(),
            });
            
            console.log("Application data saved to Firestore successfully.");
            firestoreSuccess = true;


        } catch (error) {
            console.error("Error saving application to Firestore: ", error);
            toast.error("Error saving data to our records. Please contact us directly. ❌");
            return; // Stop if the critical save fails
        }
        
        // 2. Trigger the Email Notification via a serverless proxy
        try {
            if (GMAIL_SERVICE_URL.startsWith('http')) {
                const response = await fetch(GMAIL_SERVICE_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData),
                    mode: 'cors' // Essential for cross-origin requests like Apps Script
                });
                
                if (!response.ok) {
                    throw new Error(`Email Service failed with status: ${response.status}`);
                }
                
                // If both Firestore save and email attempt succeeded
                toast.success("Application submitted successfully! We will be in touch shortly. 🚀");
            } else {
                // If URL is not configured, inform the user
                toast.info("Data saved to Firestore. Please configure the Email Service URL to enable notifications.");
            }
        } catch (emailError) {
             console.error("Error sending application email via proxy: ", emailError);
             // Even if the email fails, we don't treat it as a critical failure since the data is saved in Firestore
             if (firestoreSuccess) {
                 toast.warn("Application saved, but the email notification failed. We'll check the database. 🙏");
             }
        }

        // Reset form after successful submission (or successful save to Firestore)
        setFormData({
            name: "",
            email: "",
            phone: "",
            jobTitle: "",
            message: "",
        });
    };

    return (
        <>
            <Helmet>

  <title>
    Careers | Focus Inspection Technology | NDT Jobs PAUT TOFD UT RT MT PT
  </title>

  <meta
    name="description"
    content="
Careers at Focus Inspection Technology. Apply for NDT jobs including PAUT, TOFD, UT, RT, MT, PT, welding inspection and industrial inspection projects worldwide.
"
  />

  <meta
    name="keywords"
    content="
Focus Inspection careers,
NDT jobs,
PAUT jobs,
TOFD jobs,
UT technician jobs,
welding inspector jobs,
inspection company careers,
ndt jobs india,
industrial inspection jobs,
focus ndt jobs,
inspection technician vacancy
"
  />

  <link
    rel="canonical"
    href="https://focusinspectiontechnology.com/career"
  />

  <meta
    property="og:title"
    content="Careers | Focus Inspection Technology"
  />

  <meta
    property="og:description"
    content="Join Focus Inspection Technology for NDT inspection jobs and industrial testing projects worldwide."
  />

  <meta
    property="og:image"
    content="https://focusinspectiontechnology.com/images/Team.jpeg"
  />

  <meta
    property="og:url"
    content="https://focusinspectiontechnology.com/career"
  />

  <meta property="og:type" content="website" />

  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Careers at Focus Inspection Technology"
  />

  <meta
    name="twitter:description"
    content="Apply for NDT, PAUT, TOFD, UT, RT, MT, PT inspection jobs worldwide."
  />

  <meta
    name="twitter:image"
    content="https://focusinspectiontechnology.com/images/Team.jpeg"
  />

</Helmet>

            <div className="w-full bg-gray-50">
                {/* Hero Section */}
                <section className="relative w-full h-80 flex items-center justify-center mb-16 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-blue-800"></div>
                    <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="hexPattern" width="60" height="52" patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
                                <polygon points="30,0 60,15 60,45 30,60 0,45 0,15" stroke="white" strokeWidth="0.3" fill="none" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#hexPattern)" />
                        <circle cx="150" cy="100" r="80" stroke="white" strokeWidth="0.5" fill="none" />
                        <circle cx="600" cy="50" r="120" stroke="white" strokeWidth="0.5" fill="none" />
                    </svg>

                    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg">
                            Careers at Focus
                        </h1>
                        <p className="text-lg md:text-xl max-w-2xl mx-auto text-white">
                            Join our team of dedicated professionals and build a future in NDT.
                        </p>
                    </div>
                </section>

                {/* 1. Job Listings Section */}
                <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900">Current Openings</h2>
                        <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto mt-4">
                            We are looking for talented and passionate individuals to grow with us. Browse our open positions and apply today!
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {jobOpenings.map((job, index) => (
                            <div key={index} className="bg-white p-6 rounded-2xl shadow-xl space-y-4 border-t-4 border-blue-600 transition hover:shadow-2xl">
                                <div className="flex items-center space-x-3 text-blue-700">
                                    <Briefcase className="flex-shrink-0" />
                                    <h3 className="text-xl font-bold">{job.title}</h3>
                                </div>
                                <p className="text-gray-700 text-sm">{job.description}</p>
                                <div className="text-gray-600 pt-2">
                                    <h4 className="font-semibold mb-1 text-sm text-gray-800">Key Requirements:</h4>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        {job.requirements.map((req, i) => (
                                            <li key={i}>{req}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 2. Application Form Section */}
                <section className="bg-gray-900 py-16">
                    <div className="max-w-4xl mx-auto px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h3 className="text-4xl font-extrabold text-white">Ready to Join the Team?</h3>
                            <p className="text-gray-400 text-lg mt-3">
                                Fill out the application form below. Don't forget to include a link to your resume or cover letter in the message box!
                            </p>
                        </div>

                        <div className="bg-white/5 p-8 md:p-12 rounded-2xl shadow-2xl text-white border border-blue-700/50">
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                <div className="grid md:grid-cols-2 gap-6">
                                    {/* Name */}
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Your Full Name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    />
                                    {/* Email */}
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Your Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    />
                                    {/* Phone */}
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="Your Phone Number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    />
                                    {/* Job Title Select */}
                                    <select
                                        name="jobTitle"
                                        value={formData.jobTitle}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 rounded-lg text-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                                    >
                                        <option value="" disabled className="text-gray-400">Select a Job Title</option>
                                        {jobOpenings.map((job, index) => (
                                            <option key={index} value={job.title} className="text-gray-900">{job.title}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                {/* Message/Cover Letter */}
                                <textarea
                                    name="message"
                                    placeholder="Your Message (e.g., cover letter, link to resume)"
                                    rows="6"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
                                />
                                
                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    className="w-full bg-blue-600 hover:bg-blue-700 transition px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg text-lg mt-6"
                                >
                                    <ExternalLink size={20} />
                                    Submit Application
                                </button>
                            </form>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default Careers;
